<template>
  <div class="bxp-form">
    <h1>Just a little more <strong>information</strong>, please.</h1>
    <p class="heading-sub-text">
      Your <strong>free</strong> bountiXP experience starts soon.
    </p>

    <div class="bxp-form-fields">

      <v-text-field
        label="Company name"
        prepend-inner-icon="fas fa-tag"
        color="secondary"
        v-model="companyNameValue"
        :append-outer-icon="fields.companyName.validationIcon"
        :success="fields.companyName.valid"
        :error="fields.companyName.valid === false"
        :hint="fields.companyName.validationMessage"
        :persistent-hint="true"
      />

      <v-text-field
        label="Campaign name"
        prepend-inner-icon="fas fa-home"
        color="secondary"
        v-model="campaignNameValue"
        @keydown="validateCampaignKeyPress($event)"
        :append-outer-icon="fields.campaignName.validationIcon"
        :success="fields.campaignName.valid"
        :error="fields.campaignName.valid === false"
        :hint="fields.campaignName.validationMessage ? fields.campaignName.validationMessage : fields.campaignName.campaignSlug"
        :persistent-hint="true"
        :loading="fields.campaignName.loading"
      >
        <template v-slot:append>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="bxp-tooltip-inner">far fa-question-circle</v-icon>
            </template>
            Used as your program identifier and to determine your access URL
          </v-tooltip>
        </template>
      </v-text-field>

      <v-select
        label="Industry"
        v-model="industryValue"
        :items="fields.industry.options"
        prepend-inner-icon="fas fa-building"
        color="secondary"
        :append-outer-icon="fields.industry.validationIcon"
        :success="fields.industry.valid"
      />

      <v-select
        label="Company role"
        v-model="companyRoleValue"
        :items="fields.companyRole.options"
        prepend-inner-icon="fas fa-user-tag"
        color="secondary"
        :append-outer-icon="fields.companyRole.validationIcon"
        :success="fields.companyRole.valid"
      />

      <v-select
        label="Company size"
        v-model="numberEmployeesValue"
        :items="fields.numberEmployees.options"
        prepend-inner-icon="fas fa-users"
        color="secondary"
        :append-outer-icon="fields.numberEmployees.validationIcon"
        :success="fields.numberEmployees.valid"
      />
    </div>

    <div class="text-center">
      <bxp-button
        color="primary"
        @click="ajaxSubmitInfoForm"
        :disabled="!formValid || loading"
        :loading="loading"
      >Submit</bxp-button>
    </div>
    
    <transition name="fade-in">
      <bxp-loading-screen
        v-if="loadingScreen.show"
        :success="loadingScreen.success"
      >
        <template v-slot:loading-content>
          <bxp-loading-text />
        </template>
        <template v-slot:success-content>
          <p class="bxp_loading-text" >Have fun!</p>
        </template>
      </bxp-loading-screen>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { formBus } from "@/formBus";
import { VueMaskDirective } from "v-mask";

export default {
  directives: {
    mask: VueMaskDirective
  },
  data() {
    return {
      loading: false,
      error: false,
      loadingScreen: {
        show: false,
        success: false
      }
    };
  },
  computed: {
    fields() {
      return formBus.fields
    },
    companyNameValue: {
      get() {
        return formBus.fields.companyName.value;
      },
      set(val) {
        formBus.fields.companyName.value = val;
        let valid = null;
        let message = null;
        if (val === "") {
          valid = false;
          message = "This field is incomplete";
        } else {
          valid = true;
          message = null;
        }
        this.updateValidation("companyName", valid, message);
      }
    },
    campaignNameValue: {
      get() {
        return formBus.fields.campaignName.value;
      },
      set(val) {
        formBus.fields.campaignName.loading = true;
        val = val.trim();
        formBus.fields.campaignName.value = val;
        formBus.fields.campaignName.campaignSlug = this.getCampaignSlug(val);
        this.resetValidation("campaignName", val);
        if (val.length > 25) {
          this.updateValidation(
            "campaignName",
            false,
            "Please enter a shorter name."
          );
          formBus.fields.campaignName.loading = false;
          return
        }
        if (formBus.fields.campaignName.timer !== null) {
          clearTimeout(formBus.fields.campaignName.timer);
        }
        formBus.fields.campaignName.timer = setTimeout(
          async val => {
            const campaignNameValidation = await this.validateCampaignName(val);
            this.updateValidation(
              "campaignName",
              campaignNameValidation.valid,
              campaignNameValidation.message
            );
            formBus.fields.campaignName.loading = false;
          },
          2500,
          val
        );
      }
    },
    industryValue: {
      get() {
        return this.fields.industry.value;
      },
      set(val) {
        formBus.fields.industry.value = val;
        this.updateValidation("industry", true, null);
      }
    },
    companyRoleValue: {
      get() {
        return formBus.fields.companyRole.value;
      },
      set(val) {
        formBus.fields.companyRole.value = val;
        this.updateValidation("companyRole", true, null);
      }
    },
    numberEmployeesValue: {
      get() {
        return formBus.fields.numberEmployees.value;
      },
      set(val) {
        formBus.fields.numberEmployees.value = val;
        this.updateValidation("numberEmployees", true, null);
      }
    },
    formValid() {
      return (
        formBus.fields.companyName.valid &&
        formBus.fields.campaignName.valid &&
        formBus.fields.industry.valid &&
        formBus.fields.companyRole.valid &&
        formBus.fields.numberEmployees.valid
      );
    }
  },
  methods: {
    getCountry() {
      return this.fields.country.options.find(country => country.code === this.fields.country.value)
    },
    async ajaxSubmitInfoForm() {
      this.loading = true;
      //Verify reCAPTCHA
      const recaptchaResponse = await this.executeRecaptcha();
      if (!recaptchaResponse) {
        return false;
      }
      this.loadingScreen.show = true;
      //Build the data for the server
      const formData = new FormData();
      formData.append("action", "bxp_ajax_submit_info_form");
      formData.append("recaptcha_response", recaptchaResponse);
      formData.append("nonce", this.ajaxParams.nonce);
      formData.append("email", formBus.fields.email.value);
      formData.append("company_name", formBus.fields.companyName.value);
      formData.append("campaign_name", formBus.fields.campaignName.value);
      formData.append("industry", formBus.fields.industry.value);
      formData.append("company_role", formBus.fields.companyRole.value);
      formData.append("number_employees", formBus.fields.numberEmployees.value);
      //"Hidden" fields
      formData.append("verification_code", formBus.fields.verificationCode.value);
      formData.append("first_name", formBus.fields.firstName.value);
      formData.append("last_name", formBus.fields.lastName.value);
      formData.append("country", formBus.fields.country.value);
      formData.append("country_name", this.getCountry().name);
      formData.append("country_dial_code", this.getCountry().dialCode);
      formData.append("mobile_number", formBus.fields.mobileNumber.value);
      formData.append("plan", formBus.fields.plan.value);
      //Add the Hubspot Tracking Code and Client IP to the submission
      formData.append("hubspotutk", this.getHsTrackingCode());

      //Send to the server
      axios.post(this.ajaxParams.ajaxurl, formData).then(response => {
        if (response.data.error) {
          this.loadingScreen.show = false;
          formBus.errorMessage = response.data.error;
          formBus.error = true;
        }
        if (response.data.success) {
          this.loadingScreen.success = true;
          this.triggerGtmThankYouEvent();
          setTimeout(function() {
            window.location.href = response.data.redirect;
          }, 1500);
        }
        this.loading = false;
      });
    },
    async ajaxvalidateCampaignName() {
      //Verify reCAPTCHA
      const recaptchaResponse = await this.executeRecaptcha();
      if (!recaptchaResponse) {
        return false;
      }
      //Build the data for the server
      const formData = new FormData();
      formData.append("action", "bxp_ajax_validate_campaign_name");
      formData.append("recaptcha_response", recaptchaResponse);
      formData.append("nonce", this.ajaxParams.nonce);
      formData.append("campaign_name", formBus.fields.campaignName.value);

      //Send to the server
      return axios.post(this.ajaxParams.ajaxurl, formData).then(response => {
        if (!response) {
          return "An unknown error occurred.";
        }
        if (response.data.error) {
          return response.data;
        }
        if (response.data.success) {
          return true;
        }
      });
    },
    validateCampaignKeyPress(event) {
      if (!event.key.match(/^[a-z\d_\s]+$/i)) {
        event.preventDefault()
      }
    },
    async validateCampaignName(campaignName) {
      //Empty field
      if (campaignName === "") {
        return {
          valid: false,
          message: "This field is incomplete"
        };
      }
      //Server validation
      const validCampaignName = await this.ajaxvalidateCampaignName();
      if (validCampaignName !== true) {
        return {
          valid: false,
          message: validCampaignName.error
        };
      }
      return {
        valid: true,
        message: null
      };
    },
    /*
     *
     ** Helpers **
     *
     */
    getCampaignSlug(campaignName) {
      if (campaignName === '') {
        return null;
      }
      const subdomain = campaignName.replace(/ /g, '').toLowerCase()
      return 'https://' + subdomain + '.bountixp.com';
    }
  },
  created() {
    const code = this.$route.query.code;
    if (code !== undefined) {
      formBus.fields.verificationCode.value = code;
    }
    const email = decodeURI(this.$route.query.email);
    if (email !== undefined) {
      formBus.fields.email.value = email;
    }
    if (formBus.userData.first_name !== undefined) {
      formBus.fields.firstName.value = formBus.userData.first_name;
    }
    if (formBus.userData.last_name !== undefined) {
      formBus.fields.lastName.value = formBus.userData.last_name;
    }
    if (formBus.userData.country !== undefined) {
      formBus.fields.country.value = formBus.userData.country;
    }
    if (formBus.userData.mobile_number !== undefined) {
      formBus.fields.mobileNumber.value = formBus.userData.mobile_number;
    }
    if (formBus.userData.plan !== undefined) {
      formBus.fields.plan.value = formBus.userData.plan;
    }
  }
};
</script>
